import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Divider, IconButton, TextField, Typography } from '@material-ui/core';
import { FirstPage, LastPage } from '@material-ui/icons';
import { embedDashboard } from '@superset-ui/embedded-sdk';
import * as Styled from './styled';
import { RootStoreContext } from '../../App';
import CategorizedReportList from './CategorizedReportList';
import { LoadingDiv } from 'containers/Home/Home';
import { useTranslation } from 'react-i18next';

export default observer(function () {
    const rootStore = useContext(RootStoreContext);
    const [listCollapsed, setListCollapsed] = useState(false);
    const [supersetErrored, setSupersetErrored] = useState(false);
    const { t } = useTranslation(['reports']);

    const {
        initializeSession,
        fetchReports,
        fetchGuestToken,
        searchInput,
        setSearchInput,
        reports,
        filteredReportsByCategory,
        currentReportId,
        setReport,
        clearStore,
        loadableMap
    } = rootStore.reportsStore;
    
    useEffect(() => {
        initializeSession();
        fetchReports();
        return () => {
            clearStore();
        }
    }, []);

    useEffect(() => {
        setSupersetErrored(false);
        if (currentReportId) {
            loadDashboard();
        }
    }, [currentReportId]);
    
    const loading = loadableMap.get('fetchingReports');

    const loadDashboard = async () => {
        try {
            await embedDashboard({
                id: currentReportId,
                supersetDomain: new URL(localStorage.getItem('reportingBaseUrl') || '').origin,
                mountPoint: document.getElementById('superset-reporting')!,
                fetchGuestToken: fetchGuestToken,
                dashboardUiConfig: {
                    hideChartControls: false,
                    hideTitle: false,
                    hideTab: true,
                    urlParams: {
                        userId: localStorage.getItem('userId') || -1,
                        timekeeperId: (rootStore.appStore.currentTimekeeper && rootStore.appStore.currentTimekeeper.timeKeeperId) || -1
                    }
                }
            });
        } catch (e) {
            setSupersetErrored(true);
            rootStore.snackbarStore.triggerSnackbar(e);
        }
    };

    const onViewDashboard = (id: string) => {
        setReport(id);
        setListCollapsed(true);
    };
    
    if (loading) {
        return LoadingDiv();
    }
    
    return (
        <Styled.Container>
            {listCollapsed ? (
                <Styled.ReportListCollapsed square={true}>
                    <IconButton onClick={() => setListCollapsed(false)}>
                        <LastPage />
                    </IconButton>
                </Styled.ReportListCollapsed>
            ) : (
                <Styled.ReportList square={true}>
                    <Styled.ReportListHeader>
                        <div style={{ flex: 1 }}>
                            <TextField
                                label={t('list.field.search.label')}
                                fullWidth={true}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>
                        <IconButton onClick={() => setListCollapsed(true)}>
                            <FirstPage />
                        </IconButton>
                    </Styled.ReportListHeader>
                    <Divider />
                    {Object.keys(filteredReportsByCategory).length === 0 && (
                        <Typography style={{ padding: '14px' }}>{t('list.empty')}</Typography>
                    )}
                    {Object.keys(filteredReportsByCategory).map((dbc) => (
                        <React.Fragment key={dbc}>
                            <Divider />
                            <CategorizedReportList
                                category={dbc}
                                reports={filteredReportsByCategory[dbc]}
                                currentReportId={currentReportId}
                                setCurrentReportId={onViewDashboard}
                            />
                        </React.Fragment>
                    ))}
                </Styled.ReportList>
            )}
            <Styled.SupersetView>
                {reports.length === 0 && (
                    <Typography variant="h5" style={{ padding: 20 }}>
                        {t('details.no_reports')}
                    </Typography>
                )}
                {reports.length > 0 && !currentReportId && (
                    <Typography variant="h5" style={{ padding: 20 }}>
                        {t('details.select_report')}
                    </Typography>
                )}
                {currentReportId && supersetErrored && (
                    <Typography variant="h5" style={{ padding: 20 }}>
                        {t('details.error')}
                    </Typography>
                )}
                {currentReportId && !supersetErrored && (
                    <div style={{ height: '100%', display: 'block' }}>
                        <Styled.SuperSetBox id="superset-reporting" />
                    </div>
                )}
            </Styled.SupersetView>
        </Styled.Container>
    );
});
